import { normalizeKey } from "../../utils/normalizeString";

// normalizarChaveEtapa casava o nome de etapa vindo do backend contra as
// chaves de JCFG só com lowercase exato — qualquer variação de acentuação
// (ex.: "Promocao da Saude" sem acento, vindo de uma fonte de dados sem
// normalização) faz a etapa inteira sumir da grade de cards em Tendências,
// sem erro nenhum (o dado é descartado silenciosamente).
export function normalizeText(value) {
  return normalizeKey(value);
}

export function normalizarChaveEtapa(valor, chaves) {
  const texto = normalizeText(valor);
  return chaves.find((etapa) => normalizeText(etapa) === texto);
}

// Lê o histórico por etapa direto de /metrics (uma linha = uma etapa em um
// período, sempre). Antes isso vinha de /drivers-metrics (join com causas
// qualitativas) — uma etapa só aparecia ali se algum driver tivesse sido
// taggeado naquele período, então etapas sem causa extraída (ou sem nenhum
// driver tocado por outro motivo) sumiam da grade de cards inteiras, mesmo
// com o score da etapa existindo normalmente. /metrics não tem essa
// dependência: todo score vira card.
export function transformarMetricsEmEtapaHist(
  data,
  chavesJCFG,
  periodMetaById,
  ordenarPeriodo,
) {
  const resultado = {};

  data.forEach((item) => {
    const etapa = normalizarChaveEtapa(item.name, chavesJCFG);
    const periodo = periodMetaById[item.fk_id_period]?.name;
    const valor = Number(item.value);
    const sistemaUnimed = Number(item.unimed_system);
    if (!etapa || !periodo || !Number.isFinite(valor)) return;

    if (!resultado[etapa]) resultado[etapa] = [];
    resultado[etapa].push({
      p: periodo.split("/")[0],
      v: Math.round(valor),
      s: Number.isFinite(sistemaUnimed) ? sistemaUnimed : undefined,
      ordem: ordenarPeriodo(periodo),
    });
  });

  Object.values(resultado).forEach((hist) => {
    hist.sort((a, b) => a.ordem - b.ordem);
    hist.forEach((ponto) => {
      delete ponto.ordem;
    });
  });

  return resultado;
}

// Agrupamentos de cidade que não são uma cidade: ficam no gráfico, mas não
// entram no ranking nem no alerta de queda do resumo.
const NAO_CIDADES = new Set(["outras cidades", "nao informado"]);

// Série do NPS por cidade (/segmentation, type_description = city): uma linha
// por período, uma chave por cidade. Valor nulo ou vazio fica de fora, em vez
// de virar 0 (Number(null) === 0 desenhava um ponto falso no fundo do eixo).
// Cidade sem segmento num mês (abaixo do piso de respostas) simplesmente não
// tem a chave naquela linha.
export function transformarNpsPorCidadeParaGrafico(data, ordenarPeriodo) {
  const rotuloPorChave = {};
  const grupos = new Map();

  data
    .filter(
      (item) =>
        item.category === "nps" &&
        item.type_description === "city" &&
        item.period_name &&
        item.description &&
        item.value !== null &&
        item.value !== undefined &&
        item.value !== "",
    )
    .forEach((item) => {
      const valor = Number(item.value);
      if (!Number.isFinite(valor)) return;

      const periodo = item.period_name;
      const chaveCidade = normalizeKey(item.description);
      const cidade = (rotuloPorChave[chaveCidade] ??= String(
        item.description,
      ).trim());
      const chave = `${periodo}||${cidade}`;
      const peso = Number(item.mentions_count || 0);
      const grupo = grupos.get(chave) || {
        periodo,
        cidade,
        somaPonderada: 0,
        somaPesos: 0,
        somaSimples: 0,
        total: 0,
      };

      grupo.somaPonderada += valor * peso;
      grupo.somaPesos += peso;
      grupo.somaSimples += valor;
      grupo.total += 1;
      grupos.set(chave, grupo);
    });

  const porPeriodo = Array.from(grupos.values()).reduce((acc, grupo) => {
    const row = acc.get(grupo.periodo) || {
      mes: grupo.periodo.split("/")[0],
      ordem: ordenarPeriodo(grupo.periodo),
    };

    row[grupo.cidade] = Math.round(
      grupo.somaPesos > 0
        ? grupo.somaPonderada / grupo.somaPesos
        : grupo.somaSimples / grupo.total,
    );
    acc.set(grupo.periodo, row);
    return acc;
  }, new Map());

  return Array.from(porPeriodo.values())
    .sort((a, b) => a.ordem - b.ordem)
    .map(({ ordem, ...row }) => row);
}

export function cidadesDaSerie(serie) {
  return Array.from(
    new Set(serie.flatMap((row) => Object.keys(row).filter((k) => k !== "mes"))),
  );
}

// Eixo Y a partir dos dados, em múltiplos de 10 com folga de 5 pontos. O
// domínio fixo [40, 80] cortava cidades com NPS baixo (Viamão Set/26 = 12
// ficava colado no eixo).
export function dominioNpsPorCidade(serie, meta = null) {
  const valores = serie.flatMap((row) =>
    Object.entries(row)
      .filter(([k, v]) => k !== "mes" && Number.isFinite(v))
      .map(([, v]) => v),
  );
  if (meta !== null) valores.push(meta);
  if (valores.length === 0) return [0, 100];
  const min = Math.max(-100, Math.floor((Math.min(...valores) - 5) / 10) * 10);
  const max = Math.min(100, Math.ceil((Math.max(...valores) + 5) / 10) * 10);
  return [min, max];
}

// Texto acima do gráfico, montado dos próprios dados do cliente. Antes era
// uma frase fixa sobre cidades da região de Campinas, exibida para todos os
// clientes.
export function resumoNpsPorCidade(
  serie,
  { singular = "mês", plural = "meses" } = {},
) {
  if (serie.length === 0) return "";
  const n = serie.length;
  const janela = n === 1 ? `no último ${singular}` : `nos últimos ${n} ${plural}`;
  const frases = [`Evolução do NPS ${janela} por cidade da área de cobertura.`];

  const cidades = cidadesDaSerie(serie).filter(
    (c) => !NAO_CIDADES.has(normalizeKey(c)),
  );
  const ultimo = serie[n - 1];
  const lideres = cidades
    .filter((c) => Number.isFinite(ultimo[c]))
    .sort((a, b) => ultimo[b] - ultimo[a])
    .slice(0, 3);
  if (lideres.length > 0) {
    const lista = lideres.map((c) => `${c} (${ultimo[c]})`).join(", ");
    frases.push(
      lideres.length === 1
        ? `Maior NPS no último ${singular}: ${lista}.`
        : `Maiores NPS no último ${singular}: ${lista}.`,
    );
  }

  // Queda consistente: valor nos três últimos períodos, cada um abaixo do
  // anterior. Cidade sem segmento em algum deles não entra.
  if (n >= 3) {
    const tres = serie.slice(-3);
    const emQueda = cidades.filter((c) =>
      tres.every(
        (row, i) =>
          Number.isFinite(row[c]) && (i === 0 || row[c] < tres[i - 1][c]),
      ),
    );
    if (emQueda.length > 0) {
      frases.push(
        `${emQueda.join(", ")} ${emQueda.length === 1 ? "apresenta" : "apresentam"} queda nos últimos 3 ${plural}.`,
      );
    }
  }

  return frases.join(" ");
}
